import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin, Phone, ShieldCheck, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { CONTACT } from "../../data/publicSite";
import usePageMeta from "../../lib/usePageMeta";

const initial = { first_name: "", last_name: "", email: "", phone: "", organization: "", subject: "", message: "" };
const inputCls = "w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#4A6FA5] focus:border-transparent";

export default function ContactPage() {
  usePageMeta({
    title: "Contact | California Corporate Chaplains",
    description: "Reach California Corporate Chaplains. Onsite chaplaincy in Sacramento, Placer, El Dorado, and Nevada Counties, with remote support across California and nationwide. Send a message and we'll follow up confidentially within one business day.",
  });
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== ""));
      payload.source = "contact_form";
      await api.post("/inbox", payload);
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="contact-success">
        <div className="bg-white border border-slate-200 rounded-2xl p-8 md:p-12 text-center shadow-sm">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-green-50 text-green-600 mb-6"><CheckCircle2 className="w-7 h-7" /></div>
          <h1 className="font-heading text-3xl tracking-tight text-slate-900">Thank you.</h1>
          <p className="mt-4 text-slate-600 leading-relaxed">Your message has been received by California Corporate Chaplains. A member of our team will follow up shortly.</p>
          <Link to="/" className="mt-8 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B]">Back to home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16" data-testid="contact-page">
      <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">Contact</p>
      <h1 className="font-heading text-4xl sm:text-5xl tracking-tight text-slate-900 leading-tight">Get in touch with California Corporate Chaplains.</h1>
      <p className="mt-4 text-lg text-slate-600 leading-relaxed max-w-2xl" data-testid="contact-intro">
        Have questions about supporting your employees? Send us a message, and we'll respond within one business day.
      </p>
      <p className="mt-3 text-base text-slate-600 leading-relaxed max-w-2xl" data-testid="contact-scheduling-link">
        For organizational scheduling, please use{" "}
        <Link to="/schedule" className="text-[#4A6FA5] hover:text-[#3B597B] font-medium">Schedule a Conversation</Link>.
      </p>

      <div className="mt-10 grid lg:grid-cols-5 gap-8">
        <form onSubmit={submit} data-testid="contact-form" className="lg:col-span-3 bg-white border border-slate-200 rounded-2xl p-6 md:p-8 space-y-5 shadow-sm">
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="First name" required><input required data-testid="input-first-name" value={form.first_name} onChange={update("first_name")} className={inputCls} /></Field>
            <Field label="Last name" required><input required data-testid="input-last-name" value={form.last_name} onChange={update("last_name")} className={inputCls} /></Field>
          </div>
          <Field label="Email" required><input required type="email" data-testid="input-email" value={form.email} onChange={update("email")} className={inputCls} /></Field>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Phone"><input type="tel" data-testid="input-phone" value={form.phone} onChange={update("phone")} className={inputCls} /></Field>
            <Field label="Organization"><input data-testid="input-organization" value={form.organization} onChange={update("organization")} className={inputCls} /></Field>
          </div>
          <Field label="Subject"><input data-testid="input-subject" value={form.subject} onChange={update("subject")} className={inputCls} placeholder="A short subject line (optional)." /></Field>
          <Field label="Message" required><textarea required data-testid="textarea-message" rows={5} value={form.message} onChange={update("message")} className={`${inputCls} resize-none`} /></Field>
          <button type="submit" disabled={submitting} data-testid="submit-contact" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B] disabled:opacity-60">
            {submitting ? "Sending..." : "Send Message"} <ArrowRight className="w-4 h-4" />
          </button>
          <p className="text-xs text-slate-500 flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5 text-[#4A6FA5]" /> Please do not send confidential medical or protected health information.</p>
        </form>

        <div className="lg:col-span-2 space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <Mail className="w-5 h-5 text-[#4A6FA5]" />
            <h3 className="mt-3 font-heading text-lg font-medium text-slate-900">Email us directly</h3>
            <a href={`mailto:${CONTACT.email}`} data-testid="contact-mailto" className="mt-2 inline-flex items-center gap-2 text-[#4A6FA5] hover:text-[#3B597B] font-medium">{CONTACT.email}</a>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="contact-phone-card">
            <Phone className="w-5 h-5 text-[#4A6FA5]" />
            <h3 className="mt-3 font-heading text-lg font-medium text-slate-900">Business Office</h3>
            <a href={CONTACT.phoneHref} data-testid="contact-phone-link" className="mt-2 inline-flex items-center gap-2 text-[#4A6FA5] hover:text-[#3B597B] font-medium">{CONTACT.phone}</a>
            <p className="mt-2 text-xs text-slate-500 leading-relaxed" data-testid="contact-business-hours">{CONTACT.businessHours}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="contact-after-hours-card">
            <Phone className="w-5 h-5 text-[#4A6FA5]" />
            <h3 className="mt-3 font-heading text-lg font-medium text-slate-900">After-Hours Chaplain Support</h3>
            <a href={CONTACT.afterHoursPhoneHref} data-testid="contact-after-hours-link" className="mt-2 inline-flex items-center gap-2 text-[#4A6FA5] hover:text-[#3B597B] font-medium">{CONTACT.afterHoursPhone}</a>
            <p className="mt-2 text-xs text-slate-500 leading-relaxed">For chaplain support outside regular business office hours.</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="contact-main-office">
            <MapPin className="w-5 h-5 text-[#4A6FA5]" />
            <h3 className="mt-3 font-heading text-lg font-medium text-slate-900">Main Office</h3>
            <address className="mt-2 not-italic text-sm text-slate-600 leading-relaxed">
              915 Highland Pointe Drive, Suite 250<br />
              Roseville, CA 95678
            </address>
            <p className="mt-3 text-xs text-slate-500 leading-relaxed" data-testid="contact-main-office-note">
              Office visits by appointment. Please do not send mail to this location.
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="contact-mailing-address">
            <MapPin className="w-5 h-5 text-[#4A6FA5]" />
            <h3 className="mt-3 font-heading text-lg font-medium text-slate-900">Mailing Address</h3>
            <address className="mt-2 not-italic text-sm text-slate-600 leading-relaxed">
              California Corporate Chaplains<br />
              P.O. Box 1904<br />
              Lincoln, CA 95648
            </address>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-[#F0F4F8] p-6">
            <ShieldCheck className="w-5 h-5 text-[#4A6FA5]" />
            <h3 className="mt-3 font-heading text-lg font-medium text-slate-900">Employees of enrolled organizations</h3>
            <p className="mt-2 text-sm text-slate-700 leading-relaxed">
              Direct access to 24/7 on-call chaplain support is available through the Care Portal — separate from this general contact form, which responds within one business day.
            </p>
            <Link to="/care-portal" data-testid="contact-care-portal-link" className="mt-3 inline-flex items-center gap-2 text-[#4A6FA5] hover:text-[#3B597B] font-medium">Care Portal <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-600 leading-relaxed"><span className="font-semibold text-slate-800">Service area.</span> {CONTACT.serviceArea}</div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 mb-1.5">{label} {required && <span className="text-[#4A6FA5]">*</span>}</span>
      {children}
    </label>
  );
}
